import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Loader2, CheckCircle2, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { showXPToast } from "@/lib/xpToast";

/** Student-facing assignment list + submission form for a course (Creator Studio). */
export function AssignmentsPanel({ courseId }) {
  const [assignments, setAssignments] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [submittingId, setSubmittingId] = useState(null);

  const load = () => api.get(`/courses/${courseId}/assignments`).then((r) => setAssignments(r.data)).catch(() => setAssignments([]));
  useEffect(() => { load(); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (assignmentId) => {
    const content = (drafts[assignmentId] || "").trim();
    if (!content) { toast.error("Write your submission before sending"); return; }
    setSubmittingId(assignmentId);
    try {
      await api.post(`/courses/${courseId}/assignments/${assignmentId}/submit`, { content });
      toast.success("Assignment submitted!");
      showXPToast(0, 0, "Assignment Submitted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmittingId(null);
    }
  };

  if (assignments === null) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (assignments.length === 0) return <p className="text-sm text-slate-500 text-center py-8" data-testid="assignments-panel-empty">No assignments for this course yet.</p>;

  return (
    <div className="space-y-4" data-testid="assignments-panel">
      {assignments.map((a) => (
        <div key={a.id} className="bg-white/[0.03] border border-white/10 rounded-xl p-4" data-testid={`assignment-card-${a.id}`}>
          <p className="text-sm font-semibold text-white flex items-center gap-2"><ClipboardList className="w-4 h-4 text-violet-400" /> {a.title}</p>
          <p className="text-xs text-slate-400 mt-1.5 whitespace-pre-wrap">{a.instructions}</p>
          {a.my_submission ? (
            <div className="mt-3 bg-white/[0.03] rounded-lg p-3">
              <p className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 text-slate-400">
                {a.my_submission.status === "reviewed" ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Clock className="w-3 h-3 text-amber-400" />}
                {a.my_submission.status === "reviewed" ? `Reviewed · Grade ${a.my_submission.grade ?? "—"}%` : "Submitted · Awaiting review"}
              </p>
              <p className="text-xs text-slate-300 mt-1.5">{a.my_submission.content}</p>
              {a.my_submission.feedback && <p className="text-xs text-indigo-300 mt-1.5 italic">Feedback: {a.my_submission.feedback}</p>}
            </div>
          ) : (
            <div className="mt-3">
              <textarea
                value={drafts[a.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                placeholder="Write your submission here..." rows={3} data-testid={`assignment-submit-input-${a.id}`}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none"
              />
              <button onClick={() => submit(a.id)} disabled={submittingId === a.id} data-testid={`assignment-submit-btn-${a.id}`} className="mt-2 flex items-center gap-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 rounded-lg">
                {submittingId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Submit"}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
